import { isEmpty } from 'lodash';
import moment, { Moment } from 'moment';
import { FinancialStatementPeriodFullWrite } from 'api';
import { NOT_APPLICABLE } from 'common/constants/general';
import { ISO_DATE_FORMAT, toString } from 'utilities';
import { isLastQuarter } from 'utilities/dateUtils';
import { SelectedFinancialsPeriod } from './types';

const formatDate = (date: string | Moment) => moment(date).format(ISO_DATE_FORMAT);

const getNTMRevenueGrowth = (ltmRevenue = 0, ntmRevenue = 0) => toString(ntmRevenue / ltmRevenue - 1, NOT_APPLICABLE);

const getLTMRevenueGrowth = (ltmRevenue = 0, pastYearLTMRevenue = 0) =>
  toString(ltmRevenue / pastYearLTMRevenue - 1, NOT_APPLICABLE);

const getEbitdaMargin = (ebitda = 0, revenue = 0) => toString(ebitda / revenue, NOT_APPLICABLE);

const getGrossMargin = (revenue = 0, gross_profit = 0) => toString(gross_profit / revenue, NOT_APPLICABLE);

function calculateTangibleBookValue(selectedFinancialsPeriod: SelectedFinancialsPeriod) {
  if (isEmpty(selectedFinancialsPeriod)) {
    return 0;
  }
  const assetsTotal = selectedFinancialsPeriod?.balance_sheet?.assets_total ?? 0;
  const intangibles = selectedFinancialsPeriod?.balance_sheet?.intangibles ?? 0;
  const liabilitiesTotal = selectedFinancialsPeriod?.balance_sheet?.liabilities_total ?? 0;
  return assetsTotal - intangibles - liabilitiesTotal;
}

type GetCompanyPerfMetricsAsOfDateProps = {
  asOfDate?: string;
  financialsPeriods?: FinancialStatementPeriodFullWrite[];
  companyName?: string;
  fiscalYearEnd?: string;
};

export const getCompanyPerfMetricsAsOfDate = (props: GetCompanyPerfMetricsAsOfDateProps): any => {
  const { asOfDate, financialsPeriods, companyName, fiscalYearEnd } = props;
  if (!financialsPeriods || !asOfDate || !fiscalYearEnd) {
    return {};
  }

  const defaultFinancialsPeriod = {} as SelectedFinancialsPeriod;
  const selectedFinancialsPeriod
    = financialsPeriods.find(period => formatDate(period.statement_date) === formatDate(asOfDate))
    ||defaultFinancialsPeriod;

  const pastYearFinancialsPeriod
    = financialsPeriods.find(
      period => formatDate(period.statement_date) === formatDate(moment(asOfDate).subtract(1, 'year'))
    ) ?? defaultFinancialsPeriod;

  const nextYearFinancialsPeriod
    = financialsPeriods.find(
      period => formatDate(period.statement_date) === formatDate(moment(asOfDate).add(1, 'year'))
    ) ?? defaultFinancialsPeriod;

  // if the date is not in financialsPeriods, but it is the last quarter, the last day of the fiscal year is searched
  if (isLastQuarter(asOfDate, fiscalYearEnd) && isEmpty(selectedFinancialsPeriod))
    return getCompanyPerfMetricsAsOfDate({ asOfDate: fiscalYearEnd, financialsPeriods, companyName });

  const selectedFinancialsPeriodTotalRevenue = Number(selectedFinancialsPeriod?.income_statement?.total_revenue ?? 0);
  const selectedFinancialsPeriodEbitda = Number(selectedFinancialsPeriod?.income_statement?.ebitda ?? 0);

  const tmpCustomCompanyPerfMetrics = {
    name: companyName,
    ltm_revenue: selectedFinancialsPeriodTotalRevenue || 0,
    ltm_revenue_growth: isEmpty(pastYearFinancialsPeriod)
      ? 0
      : getLTMRevenueGrowth(
        selectedFinancialsPeriodTotalRevenue,
        Number(pastYearFinancialsPeriod?.income_statement?.total_revenue ?? 0)
      ),
    ltm_ebitda: selectedFinancialsPeriod?.income_statement?.adjusted_ebitda ?? selectedFinancialsPeriodEbitda,
    gross_margin: isEmpty(selectedFinancialsPeriod)
      ? 0
      : getGrossMargin(
        selectedFinancialsPeriodTotalRevenue,
        Number(selectedFinancialsPeriod?.income_statement?.gross_profit ?? 0)
      ),
    ebitda_margin: isEmpty(selectedFinancialsPeriod)
      ? 0
      : getEbitdaMargin(selectedFinancialsPeriodEbitda, selectedFinancialsPeriodTotalRevenue),
    ntm_revenue: Number(nextYearFinancialsPeriod?.income_statement?.total_revenue ?? 0),
    ntm_revenue_growth: isEmpty(nextYearFinancialsPeriod)
      ? 0
      : getNTMRevenueGrowth(
        selectedFinancialsPeriodTotalRevenue,
        Number(nextYearFinancialsPeriod?.income_statement?.total_revenue ?? 0)
      ),
    ntm_ebitda: Number(nextYearFinancialsPeriod?.income_statement?.ebitda ?? 0),
    tangible_book_value: calculateTangibleBookValue(selectedFinancialsPeriod as SelectedFinancialsPeriod),
    net_income: isEmpty(selectedFinancialsPeriod) ? 0 : selectedFinancialsPeriod?.income_statement?.net_income ?? 0,
  };

  return { tmpCustomCompanyPerfMetrics, rowMustBeReadOnly: !isEmpty(selectedFinancialsPeriod) };
};
